import { Avatar, Badge, Kbd, Spinner } from '@aurora/ui-kit';
import type { FrontendModule } from '@aurora/module-sdk/web';
import {
  AlertTriangle,
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  FileDown,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  Gauge,
  Globe,
  Handshake,
  Kanban,
  Landmark,
  LayoutDashboard,
  ListPlus,
  LogOut,
  Mail,
  Menu as MenuIcon,
  Moon,
  Package,
  PieChart,
  Plus,
  Puzzle,
  Receipt,
  Repeat,
  ScanLine,
  ScrollText,
  Search,
  ShieldCheck,
  Sun,
  Tag as TagIcon,
  Target,
  Timer,
  TrendingDown,
  Truck,
  UserRound,
  Users,
  Wallet,
  Wand2,
  Warehouse,
  Zap,
} from 'lucide-react';
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';

import { ModuleApiContext } from '@aurora/module-sdk/web';

import { api } from '../api/client.js';
import type { MeResponse } from '../api/types.js';
import { useLogout } from '../auth/useAuth.js';
import { usePermissions } from '../auth/usePermissions.js';
import { useModules } from '../modules/useModules.js';
import { brandingLogoUrl, useBranding } from '../state/branding.js';
import { useFeatures } from '../state/useFeatures.js';
import {
  ACCENTS,
  accentStore,
  densityStore,
  themeStore,
  useAccent,
  useDensity,
  useTheme,
} from '../state/theme.js';
import type { Accent } from '../state/theme.js';
import { tenantStore, useTenantSlug } from '../state/tenant.js';
import { buildCommands } from './commands.js';
import { CommandPalette } from './CommandPalette.js';
import { NotificationBell } from './NotificationBell.js';

export interface AppShellProps {
  me: MeResponse;
}

const isMac = navigator.platform.toUpperCase().includes('MAC');

/**
 * v6-Design: Module deklarieren ihre Nav-Icons seit jeher als kebab-case-Namen
 * (`icon: 'kanban'`) — gerendert wurde aber immer dasselbe Puzzle. Diese Map
 * macht die Deklarationen endlich sichtbar; unbekannte Namen fallen auf das
 * Puzzle zurück, damit ein neues Modul nie ohne Icon dasteht.
 */
const NAV_ICONS: Record<string, typeof Puzzle> = {
  'alert-triangle': AlertTriangle,
  building: Building2,
  'calendar-days': CalendarDays,
  'check-circle': CheckCircle2,
  'file-down': FileDown,
  'file-spreadsheet': FileSpreadsheet,
  'file-text': FileText,
  'folder-kanban': FolderKanban,
  handshake: Handshake,
  kanban: Kanban,
  landmark: Landmark,
  mail: Mail,
  package: Package,
  receipt: Receipt,
  repeat: Repeat,
  'scan-line': ScanLine,
  target: Target,
  timer: Timer,
  'trending-down': TrendingDown,
  truck: Truck,
  users: Users,
  wallet: Wallet,
  warehouse: Warehouse,
};

function NavIcon({ name }: { name?: string | undefined }) {
  const Icon = (name !== undefined ? NAV_ICONS[name] : undefined) ?? Puzzle;
  return <Icon size={16} aria-hidden />;
}

/** Ziele des globalen „+ Neu"-Menüs — Seiten öffnen ihren Dialog via ?neu=1. */
const QUICK_CREATE = [
  { label: 'Kontakt', to: '/m/crm/contacts?neu=1' },
  { label: 'Firma', to: '/m/crm/companies?neu=1' },
  { label: 'Deal', to: '/m/crm/deals?neu=1' },
  { label: 'Projekt', to: '/m/projects/list?neu=1' },
  { label: 'Termin', to: '/m/calendar/view?neu=1' },
  { label: 'Artikel / Leistung', to: '/m/catalog/items?neu=1' },
] as const;

/** Vorschau-Farben der Akzent-Auswahl (Light-Varianten). */
const ACCENT_SWATCH: Record<Exclude<Accent, 'brand'>, string> = {
  gold: '#a37f3f',
  blue: '#2f6fed',
  green: '#16a34a',
  purple: '#9333ea',
  rose: '#db2777',
  teal: '#0d9488',
};

/** API-Bridge für Modul-Frontends (Cookies, X-Tenant, Refresh übernimmt die Shell). */
const moduleApi = {
  request: <T,>(
    path: string,
    options?: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown },
  ) => api<T>(path, options ?? {}),
  currentTenant: () => tenantStore.get(),
};

export function AppShell({ me }: AppShellProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const tenantSlug = useTenantSlug();
  const logout = useLogout();
  const modules = useModules();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  /** v6-Design: „Neu anlegen" als Bottom-Sheet auf dem Telefon. */
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const createMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const accent = useAccent();
  const density = useDensity();
  const branding = useBranding();
  const logoUrl = brandingLogoUrl(branding, tenantSlug);
  const { has: hasFeature } = useFeatures();

  const frontendModules: FrontendModule[] = modules.data?.frontendModules ?? [];
  const { can } = usePermissions();

  /**
   * v6-Design: Navigation rechteabhängig. Items ohne Berechtigung fallen weg,
   * leere Gruppen verschwinden mit — ein Nutzer sieht nur, was er öffnen darf.
   * Module mit mehreren Seiten = eigene Sektion; Ein-Seiten-Module gebündelt.
   */
  const navGroups = useMemo(() => {
    const groups: Array<{
      key: string;
      title: string;
      items: FrontendModule['navItems'][number][];
    }> = [];
    const singles: FrontendModule['navItems'][number][] = [];
    for (const frontendModule of frontendModules) {
      const items = frontendModule.navItems.filter((item) => can(item.permission));
      if (items.length === 0) continue;
      if (frontendModule.navItems.length > 1) {
        groups.push({ key: frontendModule.name, title: frontendModule.title, items: [...items] });
      } else {
        singles.push(...items);
      }
    }
    if (singles.length > 0) {
      groups.push({ key: '_singles', title: 'Weitere Bereiche', items: singles });
    }
    return groups;
  }, [frontendModules, can]);

  /** System-Verwaltung: nur zeigen, was der Nutzer wirklich darf. */
  const systemItems = useMemo(
    () =>
      [
        {
          to: '/einstellungen/module',
          label: 'Module',
          Icon: Puzzle,
          permission: 'core.modules.manage',
        },
        {
          to: '/einstellungen/stammdaten',
          label: 'Stammdaten',
          Icon: Building2,
          permission: 'core.settings.manage',
        },
        {
          to: '/einstellungen/benutzer',
          label: 'Benutzer',
          Icon: Users,
          permission: 'core.users.manage',
        },
        {
          to: '/einstellungen/rollen',
          label: 'Rollen & Rechte',
          Icon: ShieldCheck,
          permission: 'core.roles.manage',
        },
        {
          feature: 'audit',
          to: '/einstellungen/audit',
          label: 'Audit-Log',
          Icon: ScrollText,
          permission: 'core.audit.read',
        },
        {
          to: '/einstellungen/felder',
          label: 'Felder',
          Icon: ListPlus,
          permission: 'core.settings.manage',
        },
        {
          feature: 'automations',
          to: '/einstellungen/automationen',
          label: 'Automationen',
          Icon: Zap,
          permission: 'core.automations.manage',
        },
        {
          feature: 'portal',
          to: '/einstellungen/kundenportal',
          label: 'Kundenportal',
          Icon: Globe,
          permission: 'core.portal.manage',
        },
        {
          feature: 'tags',
          to: '/einstellungen/tags',
          label: 'Tags',
          Icon: TagIcon,
          permission: 'core.settings.manage',
        },
      ]
        .filter((item) => can(item.permission))
        // v6/E4: abgeschaltete Funktionen verschwinden aus der Verwaltung
        .filter((item) => !('feature' in item) || hasFeature(item.feature)),
    [can, hasFeature],
  );

  // Topbar-Widgets der Module (z. B. Live-Timer) — lazy, pro Key stabil
  const topbarWidgets = useMemo<Array<{ key: string; Component: ComponentType }>>(
    () =>
      frontendModules.flatMap((frontendModule) =>
        (frontendModule.topbarWidgets ?? []).map((widget) => ({
          key: widget.key,
          Component: lazy(widget.component),
        })),
      ),
    [frontendModules],
  );

  // Topbar-Menüs schließen bei Klick außerhalb
  useEffect(() => {
    if (!createMenuOpen && !userMenuOpen) return;
    const onDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (createMenuRef.current && !createMenuRef.current.contains(target)) {
        setCreateMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [createMenuOpen, userMenuOpen]);

  // ⌘K / Ctrl+K öffnet die Palette
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const commands = useMemo(
    () =>
      buildCommands({
        frontendModules,
        onToggleTheme: () => themeStore.toggle(),
        onLogout: () => {
          logout.mutate(undefined, { onSuccess: () => void navigate('/login') });
        },
        // v6/E4: Kommandos zu abgeschalteten Funktionen verschwinden mit
      }).filter((command) => command.to !== '/auswertungen' || hasFeature('analytics')),
    // logout/navigate sind stabil; frontendModules/hasFeature sind die echten Abhängigkeiten
    [frontendModules, hasFeature],
  );

  return (
    <div className={`shell${navOpen ? ' shell--nav-open' : ''}`}>
      <button
        type="button"
        className="shell__backdrop"
        aria-label="Navigation schließen"
        onClick={() => setNavOpen(false)}
        tabIndex={navOpen ? 0 : -1}
      />
      <aside className="shell__sidebar">
        <div className="shell__brand">
          {logoUrl !== null ? (
            <img
              className="shell__brand-logo"
              src={logoUrl}
              alt={branding?.brandShort ?? branding?.brandName ?? 'Logo'}
            />
          ) : (
            <>
              <span className="shell__brand-mark">{branding?.brandMark ?? '◈'}</span>{' '}
              {branding?.brandShort ?? branding?.brandName ?? 'Aurora'}
            </>
          )}
        </div>

        <nav className="shell__nav" aria-label="Hauptnavigation" onClick={() => setNavOpen(false)}>
          <NavLink to="/" end className="shell__navlink">
            <LayoutDashboard size={16} aria-hidden /> Dashboard
          </NavLink>
          {hasFeature('reports') ? (
            <NavLink to="/berichte" className="shell__navlink">
              <BarChart3 size={16} aria-hidden /> Berichte
            </NavLink>
          ) : null}
          {hasFeature('analytics') ? (
            <NavLink to="/auswertungen" className="shell__navlink">
              <PieChart size={16} aria-hidden /> Auswertungen
            </NavLink>
          ) : null}
          {hasFeature('controlling') ? (
            <NavLink to="/controlling" className="shell__navlink">
              <Gauge size={16} aria-hidden /> Controlling
            </NavLink>
          ) : null}

          {/*
            v6-Design: vorher eine flache Liste aller Modul-Links (35 Einträge,
            ~1400px hoch). Jetzt: Module mit mehreren Seiten bekommen eine
            eigene Sektion mit Titel; Ein-Seiten-Module werden unter „Weitere
            Bereiche" gebündelt, damit kein Titel über einem einzigen,
            gleichnamigen Eintrag steht.
          */}
          {navGroups.map((group) => (
            <div key={group.key} className="shell__nav-group">
              <div className="shell__nav-section">{group.title}</div>
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} className="shell__navlink">
                  <NavIcon name={item.icon} /> {item.label}
                </NavLink>
              ))}
            </div>
          ))}

          {systemItems.length > 0 && (
            <div className="shell__nav-group">
              <div className="shell__nav-section">System</div>
              {systemItems.map((item) => (
                <NavLink key={item.to} to={item.to} className="shell__navlink">
                  <item.Icon size={16} aria-hidden /> {item.label}
                </NavLink>
              ))}
            </div>
          )}

          {/* v6/E3: Baukasten — nur auf der Agentur-Instanz (Env-Flag) */}
          {me.builder === true && can('core.modules.manage') ? (
            <div className="shell__nav-group">
              <div className="shell__nav-section">Agentur</div>
              <NavLink to="/erzeugen" className="shell__navlink">
                <Wand2 size={16} aria-hidden /> Software erzeugen
              </NavLink>
            </div>
          ) : null}
        </nav>

        <div className="shell__sidebar-footer">
          <div className="shell__user">
            <div className="shell__user-name">{me.user.displayName}</div>
            <div className="shell__user-email">{me.user.email}</div>
          </div>
        </div>
      </aside>

      <div className="shell__main">
        <header className="shell__topbar">
          <button
            type="button"
            className="shell__menu-btn"
            onClick={() => setNavOpen(true)}
            aria-label="Navigation öffnen"
          >
            <MenuIcon size={18} aria-hidden />
          </button>
          <button
            type="button"
            className="shell__search"
            onClick={() => setPaletteOpen(true)}
            aria-label="Command-Palette öffnen"
          >
            <Search size={14} aria-hidden />
            <span>Suchen …</span>
            <span className="shell__search-keys">
              <Kbd>{isMac ? '⌘' : 'Strg'}</Kbd>
              <Kbd>K</Kbd>
            </span>
          </button>

          <div className="shell__topbar-right">
            <ModuleApiContext.Provider value={moduleApi}>
              {topbarWidgets.map(({ key, Component }) => (
                <Suspense key={key} fallback={null}>
                  <Component />
                </Suspense>
              ))}
            </ModuleApiContext.Provider>
            <NotificationBell />
            <div className="topmenu" ref={createMenuRef}>
              <button
                type="button"
                className="topbar-btn"
                onClick={() => setCreateMenuOpen((open) => !open)}
                aria-label="Neu anlegen"
              >
                <Plus size={16} aria-hidden />{' '}
                {/* Text weicht auf dem Telefon; aria-label trägt die Bedeutung. */}
                <span className="topbar-btn__label">Neu</span>
              </button>
              {createMenuOpen ? (
                <div className="topmenu__pop" role="menu">
                  {QUICK_CREATE.map((item) => (
                    <button
                      key={item.to}
                      type="button"
                      className="topmenu__item"
                      role="menuitem"
                      onClick={() => {
                        setCreateMenuOpen(false);
                        void navigate(item.to);
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {/*
              Gemeinsame Hülle für beide Varianten (Auswahl bei mehreren
              Mandanten, sonst nur ein Badge) — sonst hat das Layout keinen
              stabilen Anker, um den Mandanten auf dem Telefon auszublenden.
            */}
            <span className="shell__tenant-slot">
              {me.tenants.length > 1 ? (
                <select
                  className="au-input shell__tenant"
                  value={tenantSlug ?? ''}
                  onChange={(event) => tenantStore.set(event.target.value)}
                  aria-label="Mandant wechseln"
                >
                  {me.tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.slug}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              ) : (
                <Badge tone="accent">{me.tenants[0]?.name ?? 'Kein Mandant'}</Badge>
              )}
            </span>

            <div className="topmenu" ref={userMenuRef}>
              <button
                type="button"
                className="topbar-btn topbar-btn--avatar"
                onClick={() => setUserMenuOpen((open) => !open)}
                aria-label="Benutzermenü"
              >
                <Avatar name={me.user.displayName} size={30} />
              </button>
              {userMenuOpen ? (
                <div className="topmenu__pop" role="menu">
                  <NavLink
                    to="/einstellungen/profil"
                    className="topmenu__item"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                  >
                    <UserRound size={15} aria-hidden /> Mein Profil
                  </NavLink>
                  <button
                    type="button"
                    className="topmenu__item"
                    role="menuitem"
                    onClick={() => themeStore.toggle()}
                  >
                    {theme === 'dark' ? (
                      <Sun size={15} aria-hidden />
                    ) : (
                      <Moon size={15} aria-hidden />
                    )}
                    {theme === 'dark' ? 'Helles Design' : 'Dunkles Design'}
                  </button>
                  <button
                    type="button"
                    className="topmenu__item"
                    role="menuitem"
                    onClick={() => densityStore.toggle()}
                  >
                    <ListPlus size={15} aria-hidden />
                    {density === 'compact' ? 'Komfortable Listen' : 'Kompakte Listen'}
                  </button>
                  <div className="topmenu__section">Akzentfarbe</div>
                  <div className="topmenu__accents">
                    {branding?.accentHex != null ? (
                      <button
                        type="button"
                        className={`topmenu__accent${accent === 'brand' ? ' topmenu__accent--active' : ''}`}
                        style={{ background: branding.accentHex }}
                        title="Marke"
                        aria-label="Akzentfarbe Marke"
                        onClick={() => accentStore.set('brand')}
                      />
                    ) : null}
                    {ACCENTS.map((accentOption) => (
                      <button
                        key={accentOption.key}
                        type="button"
                        className={`topmenu__accent${accent === accentOption.key ? ' topmenu__accent--active' : ''}`}
                        style={{ background: ACCENT_SWATCH[accentOption.key] }}
                        title={accentOption.label}
                        aria-label={`Akzentfarbe ${accentOption.label}`}
                        onClick={() => accentStore.set(accentOption.key)}
                      />
                    ))}
                  </div>
                  <button
                    type="button"
                    className="topmenu__item"
                    role="menuitem"
                    onClick={() =>
                      logout.mutate(undefined, { onSuccess: () => void navigate('/login') })
                    }
                  >
                    <LogOut size={15} aria-hidden /> Abmelden
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="shell__content">
          {modules.isLoading ? (
            <div className="shell__loading">
              <Spinner label="Module werden geladen …" />
            </div>
          ) : (
            <ModuleApiContext.Provider value={moduleApi}>
              <Outlet context={{ me }} />
            </ModuleApiContext.Provider>
          )}
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        commands={commands}
      />

      {/*
        v6-Design: Bottom-Navigation fürs Telefon (per CSS nur ≤900px sichtbar).
        Daumen-erreichbare Kernziele statt „alles hinter dem Burger": Start,
        Aufgaben (falls Projekte aktiv), Neu (Sheet), Suchen, Menü.
      */}
      <nav className="mobilenav" aria-label="Schnellnavigation">
        <NavLink to="/" end className="mobilenav__item">
          <LayoutDashboard size={20} aria-hidden />
          <span>Start</span>
        </NavLink>
        {frontendModules.some((frontendModule) => frontendModule.name === 'projects') &&
        can('projects.projects.read') ? (
          <NavLink to="/m/projects/tasks" className="mobilenav__item">
            <CheckCircle2 size={20} aria-hidden />
            <span>Aufgaben</span>
          </NavLink>
        ) : hasFeature('reports') ? (
          <NavLink to="/berichte" className="mobilenav__item">
            <BarChart3 size={20} aria-hidden />
            <span>Berichte</span>
          </NavLink>
        ) : null}
        <button
          type="button"
          className="mobilenav__create"
          onClick={() => setCreateSheetOpen(true)}
          aria-label="Neu anlegen"
        >
          <Plus size={22} aria-hidden />
        </button>
        <button type="button" className="mobilenav__item" onClick={() => setPaletteOpen(true)}>
          <Search size={20} aria-hidden />
          <span>Suchen</span>
        </button>
        <button type="button" className="mobilenav__item" onClick={() => setNavOpen(true)}>
          <MenuIcon size={20} aria-hidden />
          <span>Menü</span>
        </button>
      </nav>

      {createSheetOpen ? (
        <div className="mobilesheet" role="dialog" aria-modal="true" aria-label="Neu anlegen">
          <button
            type="button"
            className="mobilesheet__backdrop"
            aria-label="Schließen"
            onClick={() => setCreateSheetOpen(false)}
          />
          <div className="mobilesheet__panel">
            <div className="mobilesheet__handle" aria-hidden />
            <div className="mobilesheet__title">Neu anlegen</div>
            {QUICK_CREATE.map((item) => (
              <button
                key={item.to}
                type="button"
                className="mobilesheet__item"
                onClick={() => {
                  setCreateSheetOpen(false);
                  void navigate(item.to);
                }}
              >
                <Plus size={16} aria-hidden /> {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
